// src/utils/descargarPdf.js
// Utilidad centralizada para descargar HTML como PDF.
// Usa el backend (Puppeteer/Chrome headless) para generar PDF con texto vectorial.

// La URL del backend se resuelve dinámicamente con nodeApiPath (igual que el
// resto del sistema): en dev usa el hostname del navegador → :3001, en prod
// usa ruta relativa detrás del reverse proxy. NO hardcodear un host acá —
// hacerlo rompe el export cuando el frontend corre en otra máquina.
import { nodeApiPath } from './paths';

/**
 * Descarga HTML como PDF A4 con formato completo (texto vectorial, no imagen).
 * @param {string} html - Contenido HTML
 * @param {object} [opts] - Opciones
 * @param {string} [opts.filename='documento'] - Nombre del archivo (sin .pdf)
 * @param {string} [opts.orientation='portrait'] - 'portrait' o 'landscape'
 * @param {object} [opts.margin] - Márgenes { top, right, bottom, left } en mm string (ej: '20mm')
 * @param {'A4'|'Legal'|'Letter'} [opts.format='A4'] - Tamaño de página. 'Legal' = oficio (216×356mm)
 * @param {boolean} [opts.rawHtml=false] - Si true, el HTML se usa tal cual (sin template base). Para diseños con estilos propios.
 */
export async function descargarHtmlComoPdf(html, opts = {}) {
    if (!html) return;

    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No hay token JWT');

    const filename = (opts.filename || 'documento').replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s_-]/g, '').trim();

    const response = await fetch(nodeApiPath('pdf/from-html'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
            html,
            filename,
            orientation: opts.orientation || 'portrait',
            margin: opts.margin || { top: '20mm', right: '15mm', bottom: '20mm', left: '15mm' },
            format: opts.format || 'A4',
            rawHtml: !!opts.rawHtml,
        }),
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.mensaje || 'Error al generar PDF');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Descarga el PDF del Dashboard Ejecutivo. El HTML se arma server-side con
 * los datos frescos de cada sección — el frontend solo dispara la descarga.
 *
 * @param {object} params
 * @param {'todo'|'resumen'|'financiero'|'comunicado'|'operativo'|'comparativas'|'productividad'|'personal'} params.seccion
 * @param {string} [params.desde] - YYYY-MM-DD
 * @param {string} [params.hasta] - YYYY-MM-DD
 * @param {string} [params.preset]
 */
export async function descargarDashboardEjecutivoPdf({ seccion = 'todo', desde, hasta, preset } = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) throw new Error('No hay token JWT');

    const qs = new URLSearchParams({ seccion });
    if (desde) qs.append('desde', desde);
    if (hasta) qs.append('hasta', hasta);
    if (preset) qs.append('preset', preset);

    const response = await fetch(nodeApiPath(`dashboard-ejecutivo/exportar-pdf?${qs}`), {
        headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.mensaje || 'Error al generar PDF');
    }

    // Tomar el filename del header Content-Disposition si está
    const cd = response.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename="([^"]+)"/);
    const fechaStr = new Date().toISOString().slice(0, 10);
    const filename = match ? match[1] : `dashboard-ejecutivo-${seccion}-${fechaStr}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
